import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  FormControl,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import style from "./style";
import { LoadingButton } from "@mui/lab";
import Modal from "../../components/Modal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import dayjs from "dayjs";
import { getDateFormat } from "../../utils/dateFormatStore";

const getLeaveTypeId = (item) => item?._id || item?.id;
const getLeaveTypeName = (item) => item?.type || item?.typeName || item?.name;

const LeavePage = ({
  loading,
  handleLeave,
  leaves,
  handleCreateLeave,
  handleDeleteLeave,
  handleDeleteAdminLeaveType,
  data,
  financialYearOptions = [],
  country = "",
  onSaveComplete,
  setLoading,
  showAlert,
  selectedYears,
  handleYearChange,
  allLeaveTypes,
  addLeaveType,
  updateLeaveType,
  taxCountry,
}) => {
  const [leaveBalance, setleaveBalance] = useState({});
  const [allowance, setAllowance] = useState({});
  const [dltAllowance, setDelAllowance] = useState(false);
  const [value, setValue] = useState();
  const [leaveTax, setLeaveTax] = useState(false);
  const [leaveRate, setLeaveRate] = useState(
    data ? data?.leaveBalanceRate : ""
  );

  const [accrualMethod, setAccrualMethod] = useState({});
  const [ratePerMonth, setRatePerMonth] = useState({});
  const [yearlyCap, setYearlyCap] = useState({});
  const [financialYear, setFinancialYear] = useState({});
  const [carryToNextFY, setCarryToNextFY] = useState({});
  const [leaveTypeIds, setLeaveTypeIds] = useState({});
  const [editedRows, setEditedRows] = useState({});
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYearOptions?.[0]?.value || financialYearOptions?.[0] || ""
  );
  const [dialogOpenFor, setDialogOpenFor] = useState(null);
  const [newButtonLabel, setNewButtonLabel] = useState("");
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [newRows, setNewRows] = useState([]);
  const [newRowCounter, setNewRowCounter] = useState(0);
  const [processedAdminTypeIds, setProcessedAdminTypeIds] = useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActionItem, setSelectedActionItem] = useState(null);
  const [editingLeaveTypeId, setEditingLeaveTypeId] = useState(null);
  const [editingLeaveTypeName, setEditingLeaveTypeName] = useState("");
  const role = localStorage.getItem("role");
  const countryPermissions = JSON.parse(
    localStorage.getItem("countryPermissions") || "{}"
  );

  const permissions = Object.values(countryPermissions)[0] || {};

  const canEditLeaves = role === "SuperAdmin" || permissions?.leaves?.edit === true;

  const handleMenuClick = (event, item, isNewRow) => {
    setAnchorEl(event.currentTarget);
    setSelectedActionItem({ ...item, isNewRow });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActionItem(null);
  };

  const handleDeleteBalance = () => {
    if (selectedActionItem) {
      deleteData(selectedActionItem._id);
    }
    handleMenuClose();
  };

  const handleDeleteLeaveTypeAction = async () => {
    if (selectedActionItem) {
      let typeId;
      if (selectedActionItem.isNewRow) {
        typeId = selectedActionItem._id;
      } else {
        typeId = typeof selectedActionItem.type === "object" ? selectedActionItem.type._id : selectedActionItem.type;
      }
      if (handleDeleteAdminLeaveType) {
        await handleDeleteAdminLeaveType(typeId);
        setNewRows([]);
        setProcessedAdminTypeIds(new Set());
      }
    }
    handleMenuClose();
  };

  const handleEditLeaveTypeAction = () => {
    if (selectedActionItem) {
      let typeId, typeName;
      if (selectedActionItem.isNewRow) {
        typeId = selectedActionItem._id;
        typeName = selectedActionItem.type;
      } else {
        typeId = typeof selectedActionItem.type === "object" ? selectedActionItem.type._id : selectedActionItem.type;
        typeName = typeof selectedActionItem.type === "object" ? selectedActionItem.type.type : selectedActionItem.type;
      }
      setEditingLeaveTypeId(typeId);
      setEditingLeaveTypeName(typeName);
      setDialogOpenFor(country);
    }
    handleMenuClose();
  };

  const handleAddOrUpdateLeaveType = async () => {
    if (!dialogOpenFor || !selectedLeaveTypeId.trim()) return;

    try {
      if (editingLeaveTypeId) {
        // Edit mode - update existing leave type
        if (updateLeaveType) {
          await updateLeaveType(editingLeaveTypeId, selectedLeaveTypeId);
        }
        setEditingLeaveTypeId(null);
        setEditingLeaveTypeName("");
      } else {
        // Create mode - add new leave type
        await addLeaveType(selectedLeaveTypeId);
      }
      setSelectedLeaveTypeId("");
      setDialogOpenFor(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };


  useEffect(() => {
    if (!Array.isArray(leaves) || leaves.length === 0) return;

    setAllowance((prev) => {
      let changed = false;
      const next = { ...prev };

      leaves.forEach((leaveItem) => {
        if (Object.prototype.hasOwnProperty.call(next, leaveItem._id)) {
          delete next[leaveItem._id];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [leaves]);

  useEffect(() => {
    if (!Array.isArray(allLeaveTypes) || allLeaveTypes.length === 0) return;

    const typeNameById = allLeaveTypes.reduce((acc, item) => {
      const id = getLeaveTypeId(item);
      const typeName = getLeaveTypeName(item);
      if (id && typeName) acc[id] = typeName;
      return acc;
    }, {});

    setNewRows((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;

      let changed = false;
      const next = prev.map((row) => {
        const typeName = typeNameById[row._id];
        if (!typeName || row.type === typeName) return row;

        changed = true;
        return { ...row, type: typeName };
      });

      return changed ? next : prev;
    });

    setAllowance((prev) => {
      let changed = false;
      const next = { ...prev };

      Object.entries(typeNameById).forEach(([id, typeName]) => {
        if (
          Object.prototype.hasOwnProperty.call(next, id) &&
          next[id] !== typeName
        ) {
          next[id] = typeName;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [allLeaveTypes]);

  // Clean up newRows when leaves data changes (e.g., after API refresh)
  // Remove any rows from newRows that are now in leaves (from API)
  useEffect(() => {
    if (!Array.isArray(leaves) || leaves.length === 0) return;

    const existingTypeIds = new Set((leaves || []).map((l) => {
      const typeVal = l.type;
      return typeof typeVal === "object" ? typeVal._id : typeVal;
    }));

    setNewRows((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;

      // Remove any rows that now exist in leaves (from API)
      const filtered = prev.filter((row) => !existingTypeIds.has(row._id));

      // If any rows were removed, also clean up their state
      if (filtered.length !== prev.length) {
        const removedIds = prev
          .filter((row) => existingTypeIds.has(row._id))
          .map((row) => row._id);

        removedIds.forEach((id) => {
          setAllowance((prevAllowance) => {
            const newAllowance = { ...prevAllowance };
            delete newAllowance[id];
            return newAllowance;
          });
          setAccrualMethod((prevAccrual) => {
            const newAccrual = { ...prevAccrual };
            delete newAccrual[id];
            return newAccrual;
          });
          setRatePerMonth((prevRate) => {
            const newRate = { ...prevRate };
            delete newRate[id];
            return newRate;
          });
          setYearlyCap((prevCap) => {
            const newCap = { ...prevCap };
            delete newCap[id];
            return newCap;
          });
          setCarryToNextFY((prevCarry) => {
            const newCarry = { ...prevCarry };
            delete newCarry[id];
            return newCarry;
          });
          setLeaveTypeIds((prevIds) => {
            const newIds = { ...prevIds };
            delete newIds[id];
            return newIds;
          });
        });
      }

      return filtered;
    });
  }, [leaves]);

  useEffect(() => {
    if (!Array.isArray(allLeaveTypes) || allLeaveTypes.length === 0) {
      setNewRows([]);
      setProcessedAdminTypeIds(new Set());
      return;
    }

    // Extract IDs of types already in leaves (from API)
    const existingTypeIds = new Set((leaves || []).map((l) => {
      const typeVal = l.type;
      return typeof typeVal === "object" ? typeVal._id : typeVal;
    }));

    // Update processedAdminTypeIds to include all existing leave type IDs from API
    // This ensures we don't create duplicate rows when API data changes
    const updatedProcessedIds = new Set(processedAdminTypeIds);
    existingTypeIds.forEach((id) => updatedProcessedIds.add(id));

    const toAdd = [];
    const newAllowance = {};
    const newAccrual = {};
    const newRates = {};
    const newCaps = {};
    const newCarry = {};
    const newIds = {};

    allLeaveTypes.forEach((item) => {
      const typeName = item?.type || item?.typeName || item?.name;
      const id = item?._id;
      if (!id || !typeName) return;

      // Skip if this type ID already exists in leaves (API data)
      // This is the most important check - if API already has data, don't create a new row
      if (existingTypeIds.has(id)) {
        updatedProcessedIds.add(id); // Mark as processed
        return;
      }

      // Skip if we've already processed this admin type ID
      if (updatedProcessedIds.has(id)) {
        return;
      }

      // Add this type as a new unsaved row only if it doesn't exist anywhere
      toAdd.push({
        _id: id,
        type: typeName,
        isTaxable: false,
        leaveBalance: "",
        accrualMethod: "Fixed",
        ratePerMonth: "",
        yearlyCap: "",
        carryToNextFY: false,
        isNew: true,
      });

      newAllowance[id] = typeName;
      newAccrual[id] = "Fixed";
      newRates[id] = "";
      newCaps[id] = "";
      newCarry[id] = false;
      newIds[id] = id;
      updatedProcessedIds.add(id);
    });

    // Only add new rows if there are any to add
    if (toAdd.length > 0) {
      setNewRows((prev) => {
        // Get current newRows IDs to check for duplicates
        const currentNewRowIds = new Set((prev || []).map((r) => r._id));

        // Filter out any rows that already exist in newRows
        const uniqueToAdd = toAdd.filter((row) => !currentNewRowIds.has(row._id));

        // Also filter out any rows that exist in leaves (double safety check)
        const uniqueToAddFiltered = uniqueToAdd.filter((row) => !existingTypeIds.has(row._id));

        return [...prev, ...uniqueToAddFiltered];
      });
      setAllowance((prev) => ({ ...prev, ...newAllowance }));
      setAccrualMethod((prev) => ({ ...prev, ...newAccrual }));
      setRatePerMonth((prev) => ({ ...prev, ...newRates }));
      setYearlyCap((prev) => ({ ...prev, ...newCaps }));
      setCarryToNextFY((prev) => ({ ...prev, ...newCarry }));
      setLeaveTypeIds((prev) => ({ ...prev, ...newIds }));
    }

    // Always update processedIds to track what we've seen
    setProcessedAdminTypeIds(updatedProcessedIds);
  }, [allLeaveTypes, leaves]);

  const accrualMethodOptions = [
    { label: "Monthly Fixed", value: "Fixed" },
    { label: "Days Worked", value: "OnWorkingDays" },
  ];


  useEffect(() => {
    setLeaveRate(data?.leaveBalanceRate);
  }, [data]);

  useEffect(() => {
    if (financialYearOptions?.length > 0 && !selectedFinancialYear) {
      const defaultFY = financialYearOptions[0];
      const defaultValue =
        typeof defaultFY === "object"
          ? defaultFY.value || defaultFY.label
          : defaultFY;
      setSelectedFinancialYear(defaultValue);
    }
  }, [financialYearOptions, selectedFinancialYear]);

  const deleteData = (itemId) => {
    if (itemId?.toString().startsWith("new-")) {
      setNewRows((prev) => prev.filter((row) => row._id !== itemId));

      setAllowance((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setAccrualMethod((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setRatePerMonth((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setYearlyCap((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setFinancialYear((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      setCarryToNextFY((prev) => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
    } else {
      // Existing row - show confirmation and call API
      setValue(itemId);
      setDelAllowance(true);
    }
  };

  const handleDeleteItem = async () => {
    await handleDeleteLeave(value);
    setDelAllowance(false);
    setNewRows([]);
    setProcessedAdminTypeIds(new Set());
  };

  const handleInputChange = (id, value) => {
    setAllowance((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleInputChangeNumber = (id, value) => {
    setleaveBalance((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleTaxChange = (id) => {
    setLeaveTax((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleAccrualMethodChange = (id, value) => {
    setAccrualMethod((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleRatePerMonthChange = (id, value) => {
    // Allow empty string for clearing, or numbers with decimals
    if (value === "") {
      setRatePerMonth((prev) => ({
        ...prev,
        [id]: value,
      }));
    } else if (/^\d*\.?\d*$/.test(value)) {
      // Allow typing intermediate values (like "1." or "0.5" while typing)
      setRatePerMonth((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleRatePerMonthBlur = (id, value) => {
    // Validate on blur: ensure value is >= 1
    if (value !== "") {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 1) {
        setRatePerMonth((prev) => ({
          ...prev,
          [id]: "1",
        }));
        if (showAlert) {
          showAlert("Rate must be at least 1", "error");
        }
      }
    }
  };

  const handleYearlyCapChange = (id, value) => {
    // Allow empty string for clearing, or numbers
    if (value === "") {
      setYearlyCap((prev) => ({
        ...prev,
        [id]: value,
      }));
    } else if (/^\d+$/.test(value)) {
      // Allow typing, validation happens on blur
      setYearlyCap((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleYearlyCapBlur = (id, value) => {
    // Validate on blur: ensure value is >= 1
    if (value !== "") {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 1) {
        setYearlyCap((prev) => ({
          ...prev,
          [id]: "1",
        }));
        if (showAlert) {
          showAlert("Yearly Cap must be at least 1", "error");
        }
      }
    }
  };

  const handleCarryToNextFYChange = (id) => {
    setCarryToNextFY((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Check if Save All Changes button should be enabled
  const isSaveButtonEnabled = () => {
    const leavesArray = Array.isArray(leaves) ? leaves : [];

    // New rows में valid data है?
    const hasValidNewRow = newRows.some((newRow) => {
      const type = (allowance[newRow._id] || "").trim();
      const rate = ratePerMonth[newRow._id] || "";
      const cap = yearlyCap[newRow._id] || "";
      return type && rate && cap && selectedFinancialYear;
    });

    // Existing rows में actual change है?
    const hasExistingChange = leavesArray.some((leaveItem) => {
      const selectedFYData = financialYearOptions.find(
        (fy) =>
          (typeof fy === "object" ? fy.value || fy.label : fy) ===
          selectedFinancialYear
      );

      const currentType = allowance[leaveItem._id] ?? (typeof leaveItem.type === "object" ? leaveItem.type.type : leaveItem.type);
      const currentMethod =
        accrualMethod[leaveItem._id] ?? leaveItem.method ?? "Fixed";
      const currentRate = Number(
        ratePerMonth[leaveItem._id] ?? leaveItem.rate ?? 0
      );
      const currentCap = Number(
        yearlyCap[leaveItem._id] ?? leaveItem.yearlyCap ?? 0
      );
      const currentCarry =
        carryToNextFY[leaveItem._id] ?? leaveItem.carryToNextFY ?? false;

      const hasFYChanged =
        (selectedFYData?.start || "") !== (leaveItem.start || "") ||
        (selectedFYData?.end || "") !== (leaveItem.end || "");

      const originalType = typeof leaveItem.type === "object" ? leaveItem.type.type : leaveItem.type;

      return (
        currentType !== originalType ||
        currentMethod !== (leaveItem.method ?? "Fixed") ||
        currentRate !== Number(leaveItem.rate ?? 0) ||
        currentCap !== Number(leaveItem.yearlyCap ?? 0) ||
        currentCarry !== (leaveItem.carryToNextFY ?? false) ||
        hasFYChanged
      );
    });

    return hasValidNewRow || hasExistingChange;
  };

  const handleSaveAllChanges = async () => {
    if (setLoading) setLoading(true);
    try {
      // Global selected FY का data
      const selectedFYData = financialYearOptions.find((fy) => {
        const fyValue = typeof fy === "object" ? fy.value || fy.label : fy;
        return fyValue === selectedFinancialYear;
      });

      const leavesArray = Array.isArray(leaves) ? leaves : [];

      // ===== NEW ROWS CREATE =====
      const newLeavePromises = newRows
        .filter((newRow) => {
          const type = (allowance[newRow._id] || "").trim();
          const rate = ratePerMonth[newRow._id] || "";
          const cap = yearlyCap[newRow._id] || "";
          return (
            type !== "" && rate !== "" && cap !== "" && selectedFinancialYear
          );
        })
        .map((newRow) => {
          const payload = {
            type:
              leaveTypeIds[newRow._id] || (allowance[newRow._id] || "").trim(),
            method: accrualMethod[newRow._id] ?? "Fixed",
            rate: Number(ratePerMonth[newRow._id] ?? 0),
            yearlyCap: Number(yearlyCap[newRow._id] ?? 0),
            carryToNextFY: carryToNextFY[newRow._id] ?? false,
            country: taxCountry || "",
            start: selectedFYData?.start || "",
            end: selectedFYData?.end || "",
          };
          return handleCreateLeave(payload);
        });

      if (newLeavePromises.length > 0) {
        await Promise.all(newLeavePromises);
      }

      // ===== EXISTING ROWS UPDATE =====
      const updateLeavePromises = leavesArray
        .filter((leaveItem) => {
          // Actual values user ने enter किए हैं (fallback to original if not touched)
          const originalType = typeof leaveItem.type === "object" ? leaveItem.type.type : leaveItem.type;
          const currentType = allowance[leaveItem._id] ?? originalType;
          const currentMethod =
            accrualMethod[leaveItem._id] ?? leaveItem.method ?? "Fixed";
          const currentRate = Number(
            ratePerMonth[leaveItem._id] ?? leaveItem.rate ?? 0
          );
          const currentYearlyCap = Number(
            yearlyCap[leaveItem._id] ?? leaveItem.yearlyCap ?? 0
          );
          const currentCarry =
            carryToNextFY[leaveItem._id] ?? leaveItem.carryToNextFY ?? false;

          // Financial Year change check
          const hasFYChanged =
            (selectedFYData?.start || "") !== (leaveItem.start || "") ||
            (selectedFYData?.end || "") !== (leaveItem.end || "");

          // Compare with original
          return (
            currentType !== originalType ||
            currentMethod !== (leaveItem.method ?? "Fixed") ||
            currentRate !== Number(leaveItem.rate ?? 0) ||
            currentYearlyCap !== Number(leaveItem.yearlyCap ?? 0) ||
            currentCarry !== (leaveItem.carryToNextFY ?? false) ||
            hasFYChanged
          );
        })
        .map((leaveItem) => {
          let originalTypeId = leaveItem.type;
          if (typeof leaveItem.type === "object") {
            originalTypeId = leaveItem.type._id;
          } else {
            const matchedType = allLeaveTypes?.find(
              (t) =>
                t._id === leaveItem.type ||
                t.type === leaveItem.type ||
                t.typeName === leaveItem.type ||
                t.name === leaveItem.type
            );
            if (matchedType && matchedType._id) {
              originalTypeId = matchedType._id;
            }
          }
          const payload = {
            type: originalTypeId,
            method: accrualMethod[leaveItem._id] ?? leaveItem.method ?? "Fixed",
            rate: Number(ratePerMonth[leaveItem._id] ?? leaveItem.rate ?? 0),
            yearlyCap: Number(
              yearlyCap[leaveItem._id] ?? leaveItem.yearlyCap ?? 0
            ),
            carryToNextFY:
              carryToNextFY[leaveItem._id] ?? leaveItem.carryToNextFY ?? false,
            country: taxCountry || "",
            start: selectedFYData?.start || leaveItem.start || "",
            end: selectedFYData?.end || leaveItem.end || "",
          };
          return handleLeave(payload, leaveItem._id);
        });

      if (updateLeavePromises.length > 0) {
        await Promise.all(updateLeavePromises);
      }

      // Cleanup new rows
      setNewRows([]);
      setNewRowCounter(0);
      setProcessedAdminTypeIds(new Set());

      setAllowance({});
      setAccrualMethod({});
      setRatePerMonth({});
      setYearlyCap({});
      setCarryToNextFY({});
      setLeaveTypeIds({});

      if (onSaveComplete) await onSaveComplete();

      showAlert("All changes saved successfully", "success");
    } catch (error) {
      console.error(error);
      showAlert(error?.message || "Error saving leave settings", "error");
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const handleAddCustomButton = () => {
    if (!dialogOpenFor || !selectedLeaveTypeId.trim()) return;
    addLeaveType(selectedLeaveTypeId);
    setNewButtonLabel("");
    setSelectedLeaveTypeId("");
    setDialogOpenFor(null);
  };

  // Reset dialog when it closes
  const handleDialogClose = () => {
    setDialogOpenFor(null);
    setSelectedLeaveTypeId("");
    setEditingLeaveTypeId(null);
    setEditingLeaveTypeName("");
  };

  // Update selectedLeaveTypeId when editing mode is entered
  useEffect(() => {
    if (editingLeaveTypeId && editingLeaveTypeName) {
      setSelectedLeaveTypeId(editingLeaveTypeName);
    }
  }, [editingLeaveTypeId, editingLeaveTypeName]);

  const isLeaveTypeUnchanged =
    Boolean(editingLeaveTypeId) &&
    selectedLeaveTypeId.trim() === editingLeaveTypeName.trim();

  return (
    <>
      <>
        <Dialog
          open={Boolean(dialogOpenFor)}
          onClose={handleDialogClose}
        >
          <DialogTitle>
            {editingLeaveTypeId ? "Edit Leave Type" : "Add Leave Type"}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              placeholder="Type leave type name"
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddOrUpdateLeaveType}
              disabled={
                !selectedLeaveTypeId.trim() ||
                Boolean(isLeaveTypeUnchanged)
              }
            >
              {editingLeaveTypeId ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            mt: 3,
            ml: 3,
            mr: 2,
          }}
        >
          <Typography sx={style.gridText}>
            Leave Accrual Configuration
          </Typography>

          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            {canEditLeaves && (
              <Button
                variant="contained"
                type="submit"
                color="success"
                sx={style.addButton}
                onClick={() => setDialogOpenFor(country)}
              >
                + Add Leave Type
              </Button>
            )}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Typography sx={style.inputText}>FY</Typography>
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={selectedYears?.value || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;

                    const selectedFY = financialYearOptions.find(
                      (fy) =>
                        (typeof fy === "object" ? fy.value || fy.label : fy) ===
                        selectedValue
                    );

                    handleYearChange(selectedFY);
                  }}
                  sx={style.selectFY}
                  name="financialYear"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#aaa" }}>Select FY</span>;
                    }
                    const selectedOption = financialYearOptions?.find(
                      (item) => {
                        const itemValue =
                          typeof item === "object"
                            ? item.value || item.label
                            : item;
                        return itemValue === selected;
                      }
                    );
                    if (
                      selectedOption &&
                      selectedOption.start &&
                      selectedOption.end
                    ) {
                      return `${dayjs(selectedOption.start).format(getDateFormat())} - ${dayjs(selectedOption.end).format(getDateFormat())}`;
                    }
                    return "Select FY";
                  }}
                >
                  <MenuItem value="" disabled>
                    Select FY
                  </MenuItem>
                  {financialYearOptions?.map((option, idx) => {
                    const value =
                      typeof option === "object"
                        ? option.value || option.label
                        : option;
                    const label =
                      typeof option === "object"
                        ? option.label || option.value
                        : option;
                    const start = option.start || "";
                    const end = option.end || "";
                    return (
                      <MenuItem key={idx} value={value}>
                        {start && end
                          ? `${dayjs(start).format(getDateFormat())} - ${dayjs(
                            end
                          ).format(getDateFormat())}`
                          : label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>

            {/* Add Leave Type Button - Now at the end */}
          </Box>
        </Box>

        <Box sx={style.tableContainer}>
          <TableContainer sx={style.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                    Leave Type
                  </TableCell>
                  <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                    Accrual Method
                  </TableCell>
                  <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                    Rate (per month)
                  </TableCell>
                  <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                    Yearly Cap
                  </TableCell>
                  <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                    Carry Over to Next FY
                  </TableCell>
                  {canEditLeaves && (
                    <TableCell sx={{ ...style.tableHead, ...style.tableCell }}>
                      Actions
                    </TableCell>
                  )}
                  <TableCell sx={style.tableHead}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!Array.isArray(leaves) || leaves.length === 0) &&
                  (!newRows || newRows.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="h6">No data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {/* Existing rows from API */}
                    {Array.isArray(leaves) &&
                      leaves.map((leaveItem, index) => {
                        const leaveTypeName = typeof leaveItem.type === "object" ? leaveItem.type.type : leaveItem.type;
                        const leaveTypeId = typeof leaveItem.type === "object" ? leaveItem.type._id : leaveItem._id;
                        return (
                          <TableRow key={leaveItem._id || index}>
                            <TableCell sx={style.tableCell}>
                              <TextField
                                value={allowance[leaveItem._id] ?? leaveTypeName}
                                onChange={(e) =>
                                  handleInputChange(leaveItem._id, e.target.value)
                                }
                                sx={style.tableInput}
                                variant="outlined"
                                disabled
                                inputProps={{ readOnly: true }}
                              />
                            </TableCell>
                            <TableCell sx={style.tableCell}>
                              <Select
                                disabled={!canEditLeaves}
                                value={
                                  accrualMethod[leaveItem._id] ??
                                  leaveItem.method ??
                                  "Fixed"
                                }
                                onChange={(e) =>
                                  handleAccrualMethodChange(
                                    leaveItem._id,
                                    e.target.value
                                  )
                                }
                                sx={style.tableSelect}
                                displayEmpty
                              >
                                {accrualMethodOptions.map((option, idx) => (
                                  <MenuItem key={idx} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell sx={style.tableCell}>
                              <TextField
                                disabled={!canEditLeaves}
                                value={
                                  ratePerMonth[leaveItem._id] ??
                                  leaveItem.rate ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleRatePerMonthChange(
                                    leaveItem._id,
                                    e.target.value
                                  )
                                }
                                onBlur={(e) =>
                                  handleRatePerMonthBlur(
                                    leaveItem._id,
                                    e.target.value
                                  )
                                }
                                sx={style.tableInput}
                                variant="outlined"
                                placeholder="1.5"
                              />
                            </TableCell>
                            <TableCell sx={style.tableCell}>
                              <TextField
                                disabled={!canEditLeaves}
                                value={
                                  yearlyCap[leaveItem._id] ??
                                  leaveItem.yearlyCap ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleYearlyCapChange(
                                    leaveItem._id,
                                    e.target.value
                                  )
                                }
                                onBlur={(e) =>
                                  handleYearlyCapBlur(
                                    leaveItem._id,
                                    e.target.value
                                  )
                                }
                                sx={style.tableInput}
                                variant="outlined"
                                placeholder="18"
                              />
                            </TableCell>
                            <TableCell sx={style.tableCell}>
                              <Checkbox
                                disabled={!canEditLeaves}
                                checked={
                                  carryToNextFY[leaveItem._id] ??
                                  leaveItem.carryToNextFY ??
                                  false
                                }
                                onChange={() =>
                                  handleCarryToNextFYChange(leaveItem._id)
                                }
                              />
                            </TableCell>
                            {canEditLeaves && (
                              <TableCell>
                                <IconButton
                                  onClick={(e) => handleMenuClick(e, leaveItem, false)}
                                  sx={style.deleteIconButton}
                                >
                                  <MoreVertIcon sx={style.deleteIcon} />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    {/* New/unsaved rows */}
                    {newRows?.map((newRow, index) => (
                      <TableRow key={newRow._id}>
                        <TableCell sx={style.tableCell}>
                          <TextField
                            value={allowance[newRow._id] ?? ""}
                            sx={style.tableInput}
                            variant="outlined"
                            placeholder="Enter leave type"
                            disabled
                            inputProps={{ readOnly: true }}
                          />
                        </TableCell>
                        <TableCell sx={style.tableCell}>
                          <Select
                            value={accrualMethod[newRow._id] ?? "Fixed"}
                            onChange={(e) =>
                              handleAccrualMethodChange(
                                newRow._id,
                                e.target.value
                              )
                            }
                            sx={style.tableSelect}
                            displayEmpty
                          >
                            {accrualMethodOptions.map((option, idx) => (
                              <MenuItem key={idx} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell sx={style.tableCell}>
                          <TextField
                            value={ratePerMonth[newRow._id] ?? ""}
                            onChange={(e) =>
                              handleRatePerMonthChange(
                                newRow._id,
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleRatePerMonthBlur(newRow._id, e.target.value)
                            }
                            sx={style.tableInput}
                            variant="outlined"
                            placeholder="1.5"
                          />
                        </TableCell>
                        <TableCell sx={style.tableCell}>
                          <TextField
                            value={yearlyCap[newRow._id] ?? ""}
                            onChange={(e) =>
                              handleYearlyCapChange(newRow._id, e.target.value)
                            }
                            onBlur={(e) =>
                              handleYearlyCapBlur(newRow._id, e.target.value)
                            }
                            sx={style.tableInput}
                            variant="outlined"
                            placeholder="18"
                          />
                        </TableCell>
                        <TableCell sx={style.tableCell}>
                          <Checkbox
                            checked={carryToNextFY[newRow._id] ?? false}
                            onChange={() =>
                              handleCarryToNextFYChange(newRow._id)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e) => handleMenuClick(e, newRow, true)}
                            sx={style.deleteIconButton}
                          >
                            <MoreVertIcon sx={style.deleteIcon} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleDeleteBalance}>Delete Leave Balance</MenuItem>
              <MenuItem onClick={handleEditLeaveTypeAction}>Edit Leave Type</MenuItem>
              <MenuItem onClick={handleDeleteLeaveTypeAction}>Delete Leave Type</MenuItem>
            </Menu>
          </TableContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              px: 1,
            }}
          >
            <LoadingButton
              variant="contained"
              color="success"
              sx={style.saveAllButton}
              onClick={handleSaveAllChanges}
              loading={loading}
              disabled={!isSaveButtonEnabled()}
            >
              Save All Changes
            </LoadingButton>
          </Box>
        </Box>

        <Modal
          open={dltAllowance}
          setOpen={setDelAllowance}
          deletetText={"Delete"}
          cancelText={"Cancel"}
          title={"Delete Leave Type"}
          handleDelete={handleDeleteItem}
        >
          <Box>
            <Typography>Are you sure you want to Delete?</Typography>
          </Box>
        </Modal>
      </>
    </>
  );
};

export default LeavePage;
